import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Lock, Mail, ShieldCheck, Check, Loader2, Download, MessageSquare } from "lucide-react";
import { api } from "../lib/api";

const PRICE = "99 ريال";
const FONT_AR = "'Tajawal','IBM Plex Sans Arabic',-apple-system,system-ui,sans-serif";
const FONT_DISPLAY = "'Tajawal','IBM Plex Sans Arabic',serif";

const ensureFonts = () => {
  if (document.getElementById("course-font")) return;
  const l = document.createElement("link");
  l.id = "course-font";
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Tajawal:wght@400;500;700;800;900&display=swap";
  document.head.appendChild(l);
};

const vid = () => {
  try {
    let v = localStorage.getItem("course_vid");
    if (!v) {
      v = window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Math.random().toString(36).slice(2)}${Date.now()}`;
      localStorage.setItem("course_vid", v);
    }
    return v;
  } catch (_) { return "anon"; }
};

const track = (event, props = {}) => {
  try {
    const payload = JSON.stringify({ event, props, visitor_id: vid(), page: "checkout" });
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/course/track`;
    if (navigator.sendBeacon) navigator.sendBeacon(url, payload);
    else api.post("/course/track", JSON.parse(payload));
  } catch (_) { /* analytics never breaks the funnel */ }
};

const fireGtagATC = () => {
  try {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    if (window.__course_atc_fired) return;
    window.__course_atc_fired = true;
    window.gtag("event", "conversion", {
      send_to: "AW-18239642780/L2brCPLJhL8cEJy5q_lD",
      value: 1.0,
      currency: "EGP",
    });
  } catch (_) { /* analytics never breaks the funnel */ }
};

const fireTikTokATC = () => {
  try {
    if (typeof window === "undefined" || typeof window.ttq?.track !== "function") return;
    window.ttq.track("InitiateCheckout", {
      contents: [{ content_id: "perf_ads_os", content_type: "product", content_name: "Performance Ads OS", price: 99, quantity: 1 }],
      value: 99,
      currency: "SAR",
    });
  } catch (_) { /* analytics never breaks the funnel */ }
};

const PLATFORMS = [
  { key: "meta", label: "Meta (فيسبوك / إنستجرام)" },
  { key: "tiktok", label: "TikTok" },
  { key: "google", label: "Google" },
  { key: "not_started", label: "لم أبدأ بعد" },
];

const isValidEmail = (s) => {
  if (!s || typeof s !== "string") return false;
  const t = s.trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t) && t.length >= 5 && t.length <= 254;
};

export default function CourseCheckout() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [platform, setPlatform] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    ensureFonts();
    document.title = "Performance Ads OS — إتمام الشراء";
    track("page_view", { step: "checkout" });
    try {
      if (window.ttq?.track) window.ttq.track("ViewContent", {
        contents: [{ content_id: "perf_ads_os", content_type: "product", content_name: "Performance Ads OS", price: 99, quantity: 1 }],
        value: 99, currency: "SAR",
      });
    } catch (_) { /* never breaks */ }
  }, []);

  const submit = async () => {
    if (loading) return;
    setErr("");
    const e = email.trim().toLowerCase();
    if (!isValidEmail(e)) {
      setErr("يرجى إدخال بريد إلكتروني صحيح");
      return;
    }
    setLoading(true);
    track("cta_click", { where: "checkout-pay", platform: platform || "skipped" });
    fireGtagATC();
    fireTikTokATC();
    try {
      const { data } = await api.post("/course/checkout", {
        visitor_id: vid(),
        email: e,
        platform: platform || undefined,
      });
      window.location.href = data.checkout_url;
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "تعذّر بدء عملية الدفع. حاول مرة أخرى.");
      setLoading(false);
    }
  };

  const onEmailKey = (e) => { if (e.key === "Enter") submit(); };

  return (
    <div
      dir="rtl"
      lang="ar"
      className="min-h-screen bg-[#070C18] text-[#F0EAD9] selection:bg-[#C9A66A]/40"
      style={{ fontFamily: FONT_AR }}
      data-testid="course-checkout-root"
    >
      <nav className="sticky top-0 z-30 bg-[#070C18]/85 backdrop-blur-md border-b border-white/[0.05]">
        <div className="max-w-5xl mx-auto px-5 py-3.5 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate("/perfads")}
            data-testid="course-checkout-back"
            className="flex items-center gap-2 text-[13px] text-[#CFC3A1] hover:text-[#C9A66A] transition-colors"
          >
            <ArrowLeft className="w-4 h-4 rotate-180" /> العودة
          </button>
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#C9A66A] to-[#8A6E3D] flex items-center justify-center font-extrabold text-[#0A0F1C] text-[14px]">P</span>
            <div className="leading-tight">
              <div className="font-extrabold text-[14px] tracking-tight">Performance Ads OS</div>
              <div className="text-[10px] text-[#A7956E]">إتمام الشراء</div>
            </div>
          </div>
        </div>
      </nav>

      <main className="px-5 py-10 sm:py-16">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-[1.1fr_1fr] gap-8 lg:gap-12 items-start">
          {/* LEFT — form */}
          <div className="bg-[#0E162B] border border-white/[0.07] rounded-2xl p-6 sm:p-8" data-testid="course-checkout-form">
            <div className="text-[11.5px] tracking-[0.22em] text-[#C9A66A] font-bold mb-2">EMAIL · إتمام الشراء</div>
            <h1 className="font-extrabold text-[26px] sm:text-[34px] leading-[1.25] tracking-tight" style={{ fontFamily: FONT_DISPLAY }}>
              أين نُرسل لك <span className="text-[#C9A66A]">رابط المحتوى؟</span>
            </h1>
            <p className="mt-4 text-[14.5px] text-[#CFC3A1] leading-[1.85]">
              بعد إتمام الدفع، سترى رابط التحميل المباشر فورًا على هذه الصفحة، <span className="text-white font-semibold">وسنُرسل لك أيضًا بريدًا إلكترونيًا</span> يحتوي على نفس الرابط وكل مواد المنتج (6 دروس + Workbook + قوالب القرار).
            </p>

            <div className="mt-7">
              <label htmlFor="course-email" className="text-[13px] text-[#E7DDC0] font-bold mb-2 inline-block">
                <Mail className="w-4 h-4 inline-block ml-1.5 text-[#C9A66A]" />
                البريد الإلكتروني
              </label>
              <input
                id="course-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={onEmailKey}
                placeholder="you@example.com"
                dir="ltr"
                autoComplete="email"
                data-testid="course-checkout-email"
                className="w-full bg-[#070C18] border border-white/[0.10] focus:border-[#C9A66A] focus:outline-none focus:ring-2 focus:ring-[#C9A66A]/30 rounded-lg px-4 py-3.5 text-[15px] text-white placeholder:text-[#5A5340] transition-all"
                style={{ fontFamily: "system-ui" }}
              />
              <div className="mt-2 text-[12px] text-[#A7956E] flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-[#C9A66A]" />
                لن نشارك بريدك مع أي طرف خارجي.
              </div>
            </div>

            {/* Platform micro-survey */}
            <div className="mt-8" data-testid="course-checkout-platform">
              <label className="text-[13px] text-[#E7DDC0] font-bold mb-2.5 inline-flex items-center gap-1.5">
                <MessageSquare className="w-4 h-4 text-[#C9A66A]" />
                ما المنصة التي تُشغّل عليها إعلاناتك؟ <span className="text-[11px] text-[#A7956E] font-normal">(اختياري — يساعدنا في تخصيص التحديثات لك)</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                {PLATFORMS.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => { setPlatform(platform === p.key ? "" : p.key); track("survey_answer", { platform: p.key }); }}
                    data-testid={`course-checkout-platform-${p.key}`}
                    className={`text-right rounded-lg px-3.5 py-3 text-[13px] font-semibold border transition-all ${
                      platform === p.key
                        ? "bg-[#C9A66A]/15 border-[#C9A66A]/55 text-[#E7C98C]"
                        : "bg-[#070C18] border-white/[0.10] text-[#CFC3A1] hover:border-[#C9A66A]/30 hover:text-white"
                    }`}
                  >
                    <span className="inline-flex items-center gap-2">
                      <span className={`w-3.5 h-3.5 rounded-full border ${platform === p.key ? "bg-[#C9A66A] border-[#C9A66A]" : "border-[#5A5340]"} flex items-center justify-center`}>
                        {platform === p.key && <Check className="w-2.5 h-2.5 text-[#0A0F1C]" strokeWidth={3} />}
                      </span>
                      {p.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={submit}
              disabled={loading}
              data-testid="course-checkout-pay-btn"
              className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-gradient-to-b from-[#E2C281] to-[#C9A66A] hover:from-[#EBCD93] hover:to-[#D6B477] text-[#0A0F1C] font-extrabold text-[16px] tracking-wide rounded-full px-8 py-4 min-h-[58px] transition-all disabled:opacity-60 shadow-[0_12px_44px_-8px_rgba(201,166,106,0.55)]"
            >
              {loading
                ? <><Loader2 className="w-4 h-4 animate-spin" /> جاري فتح الدفع…</>
                : <>متابعة الدفع — {PRICE} <ArrowLeft className="w-4 h-4" /></>}
            </button>

            {err && <div className="mt-3 text-[13px] text-[#F0838F] text-center" data-testid="course-checkout-err">{err}</div>}

            <div className="mt-5 flex items-center justify-center gap-4 text-[11.5px] text-[#A7956E]">
              <span className="inline-flex items-center gap-1.5"><Lock className="w-3 h-3 text-[#C9A66A]" /> دفع آمن عبر Stripe</span>
              <span>·</span>
              <span>دفعة واحدة</span>
            </div>
          </div>

          {/* RIGHT — sticky summary */}
          <aside className="lg:sticky lg:top-24 space-y-5">
            <div className="bg-gradient-to-b from-[#15203A] to-[#0E162B] border border-[#C9A66A]/40 rounded-2xl p-6 shadow-[0_30px_80px_-25px_rgba(201,166,106,0.4)]" data-testid="course-checkout-summary">
              <div className="text-[10.5px] tracking-[0.26em] text-[#C9A66A] font-bold">ملخّص الطلب</div>
              <div className="mt-3 flex items-start justify-between gap-3">
                <div>
                  <div className="font-bold text-[16px] text-white" style={{ fontFamily: FONT_DISPLAY }}>Performance Ads OS</div>
                  <div className="text-[12px] text-[#A7956E] mt-0.5">نظام تشغيل الإعلانات المدفوعة · MVP</div>
                </div>
                <div className="text-left shrink-0" dir="ltr">
                  <div className="text-[22px] font-extrabold text-white tabular-nums">99</div>
                  <div className="text-[11px] text-[#C9A66A] font-bold">SAR</div>
                </div>
              </div>
              <div className="mt-5 border-t border-white/[0.07] pt-4 space-y-2.5 text-[13.5px] text-[#E7DDC0]">
                {[
                  "6 دروس قصيرة وواضحة",
                  "Workbook عملي",
                  "Campaign Brief + Pre-Launch Checklist",
                  "Creative Testing Matrix",
                  "Metrics Dashboard + Diagnosis Sheet",
                  "7-Day Decision Framework",
                ].map((t, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <Check className="w-4 h-4 text-[#C9A66A] shrink-0" strokeWidth={2.5} />
                    <span>{t}</span>
                  </div>
                ))}
              </div>
              <div className="mt-5 border-t border-white/[0.07] pt-4 flex items-center justify-between text-[15px] font-bold">
                <span className="text-[#CFC3A1]">المجموع</span>
                <span className="text-white" dir="ltr">99 SAR</span>
              </div>
            </div>

            <div className="bg-[#0E162B] border border-[#C9A66A]/25 rounded-2xl p-5" data-testid="course-checkout-delivery">
              <div className="flex items-start gap-3">
                <Download className="w-5 h-5 text-[#C9A66A] shrink-0 mt-0.5" />
                <div className="text-[13px] text-[#E7DDC0] leading-[1.85]">
                  <div className="font-bold text-white mb-1">طريقة التسليم</div>
                  بعد الدفع مباشرة ستصلك مواد المنتج كاملة على:
                  <ul className="mt-2 mr-4 space-y-1 list-disc marker:text-[#C9A66A]">
                    <li>صفحة النجاح فورًا — رابط تحميل مباشر</li>
                    <li>بريدك الإلكتروني — نفس الرابط للوصول السريع لاحقًا</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="text-center text-[11.5px] text-[#A7956E] leading-relaxed">
              بمتابعتك أنت توافق على شروط الاستخدام وسياسة الخصوصية. هذا منتج تعليمي ولا يضمن نتائج إعلانية محددة.
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
